
import { computed, defineComponent, onMounted, nextTick, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { useI18n } from "vue-i18n/index";
import { reinitializeComponents } from "@/core/plugins/keenthemes";

// Account type
interface Step1 {
  account_type: string;
}

// Account info
interface Step2 {
  invoice_plan: string;
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface KTCreateApp extends Step1, Step2 {}

export default defineComponent({
  name: "sign-up",
  setup() {
    const submitButton = ref<HTMLButtonElement | null>(null);
    const formSignUp = ref<HTMLFormElement>();
    const currentStepIndex = ref(1);
    const store = useStore();
    const router = useRouter();

    const { t, te } = useI18n();

    const formData = ref<KTCreateApp>({
      account_type: "custom",
      invoice_plan: "100",
      name: "",
      email: "",
      phone: "",
      password: "",
    });

    onMounted(() => {
      nextTick(() => {
        reinitializeComponents();
      });
    });

    const translate = (text) => {
      if (te(text)) {
        return t(text);
      } else {
        return text;
      }
    };

    const countries = {
      en: {
        flag: "/media/flags/united-states.svg",
        name: "English",
      },
      ar: {
        flag: "/media/flags/saudi-arabia.svg",
        name: "Arabic",
      },
    };

    const signUpValidator = ref({
      name: [
        {
          required: true,
          message: translate("nameRequired"),
          trigger: "change",
        },
      ],
      email: [
        {
          required: true,
          message: translate("emailRequired"),
          trigger: "change",
        },
        {
          type: "email",
          message: translate("emailInvalid"),
          trigger: "change",
        },
      ],
      phone: [
        {
          required: true,
          message: translate("phoneRequired"),
          trigger: "change",
        },
        {
          pattern: /^(\+?\d{1,3}[- ]?)?\d{10}$/,
          message: translate("phoneNumberInvalid"),
          trigger: "change",
        },
      ],
    });

    const currentLanguage = computed(() => {
      return store.getters.getLanguage;
    });

    const currentLangugeLocale = computed(() => {
      return countries[currentLanguage.value];
    });

    //Form submit function
    const signUp = () => {
      if (!formSignUp.value) {
        return;
      }
      formSignUp.value.validate((valid) => {
        if (valid) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          // Activate loading indicator
          submitButton.value?.setAttribute("data-kt-indicator", "on");
          Swal.fire({
            title: translate("pleaseWait"),
            text: translate("creatingAccount"),
            showConfirmButton: false,
            allowOutsideClick: false,
            onBeforeOpen: () => {
              Swal.showLoading();
            },
          });
          if (formData.value.account_type === "custom") {
            // Clear existing errors
            store.dispatch(Actions.LOGOUT);
            store
              .dispatch(Actions.REGISTER, formData.value)
              .then(() => {
                Swal.fire({
                  text: translate("register_success"),
                  icon: "success",
                  buttonsStyling: false,
                  confirmButtonText: translate("ok"),
                  customClass: {
                    confirmButton: "btn fw-bold btn-light-primary",
                  },
                }).then(function () {
                  router.push({ name: "home" });
                });
              })
              .catch(() => {
                Swal.fire({
                  text: store.getters.getErrors.message,
                  icon: "error",
                  buttonsStyling: false,
                  confirmButtonText: translate("tryAgain"),
                  customClass: {
                    confirmButton: "btn fw-bold btn-light-danger",
                  },
                });
              });
          } else {
            store
              .dispatch(Actions.CONTACT_FORM, formData.value)
              .then((response) => {
                Swal.fire({
                  text: response.message,
                  icon: "success",
                  buttonsStyling: false,
                  confirmButtonText: translate("ok"),
                  customClass: {
                    confirmButton: "btn fw-bold btn-light-primary",
                  },
                }).then(function () {
                  router.push({ name: "sign-in" });
                });
              });
          }
          submitButton.value?.removeAttribute("data-kt-indicator");
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        }
      });
    };

    return {
      signUp,
      formSignUp,
      signUpValidator,
      currentLanguage,
      currentLangugeLocale,
      currentStepIndex,
      formData,
      submitButton,
    };
  },
});
